'use client';

import { triggerLandingPreview } from '@/actions/landings-proxy/landings-proxy';
import { addPreviewEntry, deletePreviewEntry, updatePreviewUrlEntry } from '@/actions/preview';
import { TenantDeploy } from '@/services/tenant-deploys';
import { generatePreviewUrlAlias, getPreviewUrl } from '@/utils/preview';
import { Button, CopyToClipboard, Select, toast } from '@payloadcms/ui';
import { OptionObject } from 'payload';
import { useState } from 'react';

export const PreviewClient = ({
  tenant,
  userEmail,
  tenantId,
  deployDocs,
}: {
  deployDocs: TenantDeploy[];
  tenant: string;
  tenantId: string;
  userEmail: string;
}) => {
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [deployConfig, setDeployConfig] = useState<OptionObject | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewResponseSuccess, setPreviewResponseSuccess] = useState(false);
  const [allowDrafts, setAllowDrafts] = useState(false);

  const deploySelectOptions = deployDocs.map((deploy) => ({
    label: deploy.bucket,
    value: deploy.id,
  }));

  const handleGeneratePreviewClick = async () => {
    setIsGeneratingPreview(true);

    const bucket = deployConfig!.label as string;
    const deployConfigDocumentId = deployConfig!.value;

    const emptyPreviewEntry = await addPreviewEntry(tenantId, deployConfigDocumentId, 'temporary-preview-url');
    const emptyPreviewEntryId = String(emptyPreviewEntry.id);

    const previewUrlAlias = generatePreviewUrlAlias(emptyPreviewEntryId, bucket);
    const computedPreviewUrl = getPreviewUrl(previewUrlAlias);
    setPreviewUrl(computedPreviewUrl);

    const response = await triggerLandingPreview({
      landing_name: tenant,
      user_email: userEmail,
      landing_bucket: bucket,
      preview_url: computedPreviewUrl,
      preview_alias: previewUrlAlias,
      allow_drafts: allowDrafts,
    });

    if (!response.ok) {
      toast.error(`Preview link for ${tenant} with deploy configuration ${deployConfig!.label} failed to generate.`);

      await deletePreviewEntry(emptyPreviewEntryId);

      setIsGeneratingPreview(false);

      return;
    }

    await updatePreviewUrlEntry(emptyPreviewEntryId, computedPreviewUrl);

    setPreviewResponseSuccess(true);

    if (userEmail.endsWith('@feverup.com')) {
      toast.success(
        `Preview link with deploy configuration ${deployConfig!.label} is being generated. You will be notified through slack once it is ready to be accessed, only if your email is registered in slack as a user.`,
      );

      setIsGeneratingPreview(false);
      return;
    }

    toast.success(
      `Preview link with deploy configuration ${deployConfig!.label} is being generated. In some minutes you will be able to access it.`,
    );
    setIsGeneratingPreview(false);
  };

  const handleSelectChange = (val: OptionObject) => {
    setPreviewUrl(null);
    setDeployConfig(val);
  };

  const getButtonText = (): string => {
    if (!deployConfig) return '';
    if (isGeneratingPreview) return `Generating preview for ${deployConfig.label}...`;
    return `Generate preview${deployConfig.label ? ` for ${deployConfig.label}` : ''}`;
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <Select
        options={deploySelectOptions}
        placeholder="Select a deploy configuration to generate a preview link from"
        onChange={(val) => {
          handleSelectChange(val as OptionObject);
        }}
      />
      {deployConfig && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '12px 0' }}>
            <input
              type="checkbox"
              checked={allowDrafts}
              onChange={(event) => setAllowDrafts(event.target.checked)}
              disabled={isGeneratingPreview}
            />
            Include draft contents (preview unapproved changes)
          </label>
          <Button
            onClick={() => {
              handleGeneratePreviewClick();
            }}
            disabled={isGeneratingPreview || !deployConfig}
          >
            {getButtonText()}
          </Button>
        </>
      )}
      {previewUrl && previewResponseSuccess && !isGeneratingPreview && (
        <>
          <p style={{ fontWeight: 'bold' }}>
            Once the preview is generated, you will be able to access it at:{' '}
            <a style={{ textDecoration: 'underline', color: 'blue' }} href={previewUrl} target="_blank" rel="noopener noreferrer">
              {previewUrl}
            </a>
            <CopyToClipboard value={previewUrl} />
          </p>
          <small>Preview links expire in 90 days.</small>
        </>
      )}
    </div>
  );
};

export default PreviewClient;
